import React from 'react'
import { Box, Button, Dialog, IconButton, Typography, CircularProgress, TextField } from '@mui/material'

export type ConfirmDialogVariant = 'deposit' | 'withdraw' | 'danger' | 'warning' | 'success' | 'info' | 'error'

export interface ConfirmDialogProps {
    open: boolean
    onClose: () => void
    onConfirm: () => void
    loading?: boolean

    title?: string
    message?: string
    confirmText?: string
    cancelText?: string

    variant?: ConfirmDialogVariant

    details?: Array<{ label: string; value: string; sub?: string; mono?: boolean }>

    notice?: string

    confirmationText?: string

    confirmationLabel?: React.ReactNode
}


const VARIANT_CONFIG: Record<ConfirmDialogVariant, {
    accent: string
    accentAlpha: string
    headerGradient: string
    iconPath: React.ReactNode
}> = {
    deposit: {
        accent: '#10b981',
        accentAlpha: '#10b98120',
        headerGradient: 'linear-gradient(135deg, #f2674a 0%, #d94f35 100%)',
        iconPath: (
            <path d="M12 3v10m0 0l-3.5-3.5M12 13l3.5-3.5M5 19h14" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        ),
    },
    withdraw: {
        accent: '#ef4444',
        accentAlpha: '#ef444420',
        headerGradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        iconPath: (
            <path d="M12 21V11m0 0l-3.5 3.5M12 11l3.5 3.5M5 5h14" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        ),
    },
    danger: {
        accent: '#ef4444',
        accentAlpha: '#ef444420',
        headerGradient: 'linear-gradient(135deg, #991b1b 0%, #7f1d1d 100%)',
        iconPath: (
            <>
                <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10 11v4M14 11v4" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </>
        ),
    },
    warning: {
        accent: '#f59e0b',
        accentAlpha: '#f59e0b20',
        headerGradient: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
        iconPath: (
            <>
                <path d="M12 4L2 20h20L12 4z" stroke="white" strokeWidth="1.8" strokeLinejoin="round" />
                <path d="M12 10v5M12 17v.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </>
        ),
    },
    success: {
        accent: '#10b981',
        accentAlpha: '#10b98120',
        headerGradient: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        iconPath: (
            <>
                <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="1.8" />
                <path d="M8 12l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </>
        ),
    },
    info: {
        accent: '#3b82f6',
        accentAlpha: '#3b82f620',
        headerGradient: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        iconPath: (
            <>
                <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="1.8" />
                <path d="M12 8v.5M12 11v5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </>
        ),
    },
    error: {
        accent: '#ef4444',
        accentAlpha: '#ef444420',
        headerGradient: 'linear-gradient(135deg, #7f1d1d 0%, #450a0a 100%)',
        iconPath: (
            <>
                <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="1.8" />
                <path d="M12 7v6M12 15v2" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </>
        ),
    },
}


const IllustrationSVG: React.FC<{ variant: ConfirmDialogVariant }> = ({ variant }) => {
    if (variant === 'deposit') return (
        <svg width="359" height="150" viewBox="0 0 359 150" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="27" y="110" width="14" height="20" rx="2" fill="#f2674a" opacity="0.18" />
            <rect x="50" y="90" width="14" height="40" rx="2" fill="#f2674a" opacity="0.28" />
            <rect x="73" y="70" width="14" height="60" rx="2" fill="#f2674a" opacity="0.38" />
            <rect x="96" y="50" width="14" height="80" rx="2" fill="#f2674a" opacity="0.5" />
            <rect x="119" y="30" width="14" height="100" rx="2" fill="#f2674a" opacity="0.65" />
            <line x1="245" y1="130" x2="245" y2="28" stroke="#f2674a" strokeWidth="3" strokeLinecap="round" />
            <polyline points="231,52 245,26 259,52" fill="none" stroke="#f2674a" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
            <circle cx="245" cy="78" r="44" stroke="#f2674a" strokeWidth="1" strokeDasharray="5,4" opacity="0.3" />
            <circle cx="245" cy="78" r="9" fill="#f2674a" opacity="0.2" />
            <circle cx="245" cy="78" r="5" fill="#f2674a" opacity="0.7" />
            <circle cx="289" cy="78" r="5" fill="#f2674a" opacity="0.5" />
            <line x1="285" y1="44" x2="293" y2="36" stroke="#f2674a" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
            <line x1="295" y1="52" x2="305" y2="50" stroke="#f2674a" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
            <line x1="285" y1="112" x2="293" y2="120" stroke="#f2674a" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
            <line x1="20" y1="136" x2="339" y2="136" stroke="#2f2f2f" strokeWidth="1" strokeLinecap="round" />
        </svg>
    )

    if (variant === 'withdraw') return (
        <svg width="359" height="150" viewBox="0 0 359 150" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="27" y="20" width="14" height="20" rx="2" fill="#ef4444" opacity="0.18" />
            <rect x="50" y="20" width="14" height="40" rx="2" fill="#ef4444" opacity="0.28" />
            <rect x="73" y="20" width="14" height="60" rx="2" fill="#ef4444" opacity="0.38" />
            <rect x="96" y="20" width="14" height="80" rx="2" fill="#ef4444" opacity="0.5" />
            <rect x="119" y="20" width="14" height="100" rx="2" fill="#ef4444" opacity="0.65" />
            <line x1="245" y1="22" x2="245" y2="122" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" />
            <polyline points="231,98 245,124 259,98" fill="none" stroke="#ef4444" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
            <circle cx="245" cy="72" r="44" stroke="#ef4444" strokeWidth="1" strokeDasharray="5,4" opacity="0.3" />
            <circle cx="245" cy="72" r="9" fill="#ef4444" opacity="0.2" />
            <circle cx="245" cy="72" r="5" fill="#ef4444" opacity="0.7" />
            <circle cx="289" cy="72" r="5" fill="#ef4444" opacity="0.5" />
            <line x1="285" y1="40" x2="295" y2="38" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
            <line x1="285" y1="104" x2="295" y2="108" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" opacity="0.35" />
            <line x1="20" y1="136" x2="339" y2="136" stroke="#2f2f2f" strokeWidth="1" strokeLinecap="round" />
        </svg>
    )

    if (variant === 'danger') return (
        <svg width="359" height="150" viewBox="0 0 359 150" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M179 18 L255 52 L255 100 Q255 136 179 152 Q103 136 103 100 L103 52 Z" fill="#ef4444" opacity="0.07" stroke="#ef4444" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M179 36 L237 62 L237 96 Q237 122 179 136 Q121 122 121 96 L121 62 Z" fill="#ef4444" opacity="0.08" stroke="#ef4444" strokeWidth="1" strokeLinejoin="round" strokeDasharray="5,4" />
            <line x1="160" y1="72" x2="198" y2="110" stroke="#ef4444" strokeWidth="3.5" strokeLinecap="round" />
            <line x1="198" y1="72" x2="160" y2="110" stroke="#ef4444" strokeWidth="3.5" strokeLinecap="round" />
            <polyline points="40,136 66,108 92,128 118,100 144,120 170,92 196,112 222,84 248,104 274,136" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.3" />
            <circle cx="44" cy="30" r="4" fill="#ef4444" opacity="0.2" />
            <circle cx="315" cy="30" r="4" fill="#ef4444" opacity="0.2" />
            <circle cx="44" cy="136" r="4" fill="#ef4444" opacity="0.12" />
            <circle cx="315" cy="136" r="4" fill="#ef4444" opacity="0.12" />
        </svg>
    )

    if (variant === 'warning') return (
        <svg width="359" height="150" viewBox="0 0 359 155" fill="none" xmlns="http://www.w3.org/2000/svg">
            <polygon points="179,16 318,142 40,142" fill="#f59e0b" opacity="0.07" stroke="#f59e0b" strokeWidth="1.5" strokeLinejoin="round" />
            <polygon points="179,38 298,134 60,134" fill="none" stroke="#f59e0b" strokeWidth="1" strokeLinejoin="round" strokeDasharray="5,4" opacity="0.45" />
            <rect x="172" y="60" width="14" height="44" rx="6" fill="#f59e0b" opacity="0.85" />
            <circle cx="179" cy="120" r="7" fill="#f59e0b" opacity="0.85" />
            <line x1="179" y1="8" x2="179" y2="0" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
            <line x1="191" y1="11" x2="198" y2="4" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
            <line x1="167" y1="11" x2="160" y2="4" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
            <line x1="76" y1="148" x2="76" y2="140" stroke="#f59e0b" strokeWidth="1" strokeLinecap="round" opacity="0.3" />
            <line x1="116" y1="148" x2="116" y2="140" stroke="#f59e0b" strokeWidth="1" strokeLinecap="round" opacity="0.3" />
            <line x1="242" y1="148" x2="242" y2="140" stroke="#f59e0b" strokeWidth="1" strokeLinecap="round" opacity="0.3" />
            <line x1="282" y1="148" x2="282" y2="140" stroke="#f59e0b" strokeWidth="1" strokeLinecap="round" opacity="0.3" />
        </svg>
    )

    if (variant === 'success') return (
        <svg width="359" height="150" viewBox="0 0 359 150" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="179" cy="72" r="62" stroke="#10b981" strokeWidth="1" strokeDasharray="6,4" opacity="0.22" />
            <circle cx="179" cy="72" r="46" stroke="#10b981" strokeWidth="1.2" opacity="0.38" />
            <circle cx="179" cy="72" r="32" fill="#10b981" opacity="0.1" />
            <circle cx="179" cy="72" r="22" fill="#10b981" opacity="0.14" />
            <polyline points="160,72 174,87 200,55" fill="none" stroke="#10b981" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="30,118 54,104 78,112 102,90" fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.4" />
            <polyline points="256,90 280,74 304,82 328,62" fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.4" />
            <circle cx="102" cy="90" r="4" fill="#10b981" opacity="0.55" />
            <circle cx="256" cy="90" r="4" fill="#10b981" opacity="0.55" />
            <circle cx="179" cy="10" r="5" fill="#10b981" opacity="0.35" />
            <circle cx="235" cy="30" r="4" fill="#10b981" opacity="0.25" />
            <circle cx="123" cy="30" r="4" fill="#10b981" opacity="0.25" />
        </svg>
    )

    if (variant === 'error') return (
        <svg width="359" height="150" viewBox="0 0 359 150" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="179" cy="72" r="58" stroke="#ef4444" strokeWidth="1" strokeDasharray="3,6" opacity="0.2" />
            <circle cx="179" cy="72" r="44" stroke="#ef4444" strokeWidth="1.2" strokeDasharray="8,5" opacity="0.3" />
            <circle cx="179" cy="72" r="30" fill="#ef4444" opacity="0.08" />
            <circle cx="179" cy="72" r="20" fill="#ef4444" opacity="0.12" />
            <line x1="163" y1="56" x2="195" y2="88" stroke="#ef4444" strokeWidth="5" strokeLinecap="round" />
            <line x1="195" y1="56" x2="163" y2="88" stroke="#ef4444" strokeWidth="5" strokeLinecap="round" />
            <polyline points="30,60 54,74 78,68 102,90" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.35" />
            <circle cx="102" cy="90" r="3.5" fill="#ef4444" opacity="0.5" />
            <polyline points="256,90 280,106 304,98 328,118" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.35" />
            <circle cx="256" cy="90" r="3.5" fill="#ef4444" opacity="0.5" />
            <rect x="30" y="100" width="10" height="28" rx="2" fill="#ef4444" opacity="0.15" />
            <rect x="44" y="88" width="10" height="40" rx="2" fill="#ef4444" opacity="0.22" />
            <rect x="58" y="110" width="10" height="18" rx="2" fill="#ef4444" opacity="0.15" />
            <rect x="291" y="96" width="10" height="32" rx="2" fill="#ef4444" opacity="0.15" />
            <rect x="305" y="84" width="10" height="44" rx="2" fill="#ef4444" opacity="0.22" />
            <rect x="319" y="104" width="10" height="24" rx="2" fill="#ef4444" opacity="0.15" />
            <circle cx="140" cy="24" r="3" fill="#ef4444" opacity="0.25" />
            <circle cx="179" cy="14" r="4" fill="#ef4444" opacity="0.2" />
            <circle cx="218" cy="24" r="3" fill="#ef4444" opacity="0.25" />
            <line x1="179" y1="20" x2="179" y2="12" stroke="#ef4444" strokeWidth="1.2" strokeLinecap="round" opacity="0.3" />
            <line x1="210" y1="28" x2="218" y2="22" stroke="#ef4444" strokeWidth="1.2" strokeLinecap="round" opacity="0.25" />
            <line x1="148" y1="28" x2="140" y2="22" stroke="#ef4444" strokeWidth="1.2" strokeLinecap="round" opacity="0.25" />
            <line x1="20" y1="136" x2="339" y2="136" stroke="#2f2f2f" strokeWidth="1" strokeLinecap="round" />
        </svg>
    )

    return (
        <svg width="359" height="150" viewBox="0 0 359 150" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="30" y1="128" x2="220" y2="128" stroke="#2f2f2f" strokeWidth="1" strokeLinecap="round" />
            <line x1="30" y1="90" x2="215" y2="90" stroke="#2a2a2a" strokeWidth="0.8" strokeDasharray="3,3" />
            <line x1="30" y1="58" x2="215" y2="58" stroke="#2a2a2a" strokeWidth="0.8" strokeDasharray="3,3" />
            <rect x="38" y="88" width="18" height="40" rx="2" fill="#10b981" opacity="0.6" />
            <line x1="47" y1="78" x2="47" y2="88" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
            <line x1="47" y1="128" x2="47" y2="138" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
            <rect x="68" y="66" width="18" height="62" rx="2" fill="#10b981" opacity="0.6" />
            <line x1="77" y1="54" x2="77" y2="66" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
            <line x1="77" y1="128" x2="77" y2="140" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
            <rect x="98" y="74" width="18" height="54" rx="2" fill="#ef4444" opacity="0.6" />
            <line x1="107" y1="62" x2="107" y2="74" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
            <line x1="107" y1="128" x2="107" y2="140" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
            <rect x="128" y="48" width="18" height="80" rx="2" fill="#ef4444" opacity="0.6" />
            <line x1="137" y1="34" x2="137" y2="48" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
            <line x1="137" y1="128" x2="137" y2="142" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
            <rect x="158" y="38" width="18" height="90" rx="2" fill="#10b981" opacity="0.6" />
            <line x1="167" y1="24" x2="167" y2="38" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
            <line x1="167" y1="128" x2="167" y2="142" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
            <circle cx="288" cy="62" r="38" stroke="#3b82f6" strokeWidth="1.2" opacity="0.32" />
            <circle cx="288" cy="62" r="28" fill="#3b82f6" opacity="0.09" />
            <circle cx="288" cy="49" r="4" fill="#3b82f6" opacity="0.8" />
            <rect x="284" y="57" width="8" height="22" rx="4" fill="#3b82f6" opacity="0.8" />
        </svg>
    )
}


const CloseCircleIcon = () => (
    <svg width="32" height="32" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 33C26.25 33 33 26.25 33 18C33 9.75 26.25 3 18 3C9.75 3 3 9.75 3 18C3 26.25 9.75 33 18 33Z" stroke="white" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M13.755 22.245L22.245 13.755" stroke="white" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22.245 22.245L13.755 13.755" stroke="white" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)


const PopupDialog: React.FC<ConfirmDialogProps> = ({
    open,
    onClose,
    onConfirm,
    loading = false,
    title = 'Are you sure?',
    message = 'Please confirm you want to proceed with this action.',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'deposit',
    details,
    notice,
    confirmationText,
    confirmationLabel,
}) => {
    const cfg = VARIANT_CONFIG[variant]
    const [typedValue, setTypedValue] = React.useState('')

    React.useEffect(() => {
        if (!open) setTypedValue('')
    }, [open])

    const requiresTyping = !!confirmationText
    const isMatch = !requiresTyping || typedValue.trim() === confirmationText
    const isConfirmDisabled = loading || !isMatch

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: '#242424',
                    borderRadius: '20px',
                    boxShadow: `0 0 60px ${cfg.accent}20`,
                    border: '1px solid #333',
                    overflow: 'hidden',
                },
            }}
        >
            <Box
                sx={{
                    background: cfg.headerGradient,
                    px: 3, pt: 2.5, pb: 2,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -28, right: -16, width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }} />
                <Box sx={{ position: 'absolute', bottom: -36, left: 50, width: 72, height: 72, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 44, height: 44, borderRadius: '12px',
                            background: 'rgba(255,255,255,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                {cfg.iconPath}
                            </svg>
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>{title}</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, mt: 0.3 }}>Review carefully before proceeding</Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small" disabled={loading} sx={{ ml: 1, flexShrink: 0 }}>
                        <CloseCircleIcon />
                    </IconButton>
                </Box>
            </Box>

            <Box sx={{ px: 3, pt: 2, pb: 3 }}>

                <Box sx={{ display: 'flex', justifyContent: 'center', mx: -3, mt: -2, mb: 1, overflow: 'hidden' }}>
                    <IllustrationSVG variant={variant} />
                </Box>

                <Typography sx={{ color: '#d0d0d0', fontSize: 14, textAlign: 'center', lineHeight: 1.6, mb: 2 }}>
                    {message}
                </Typography>

                {details && details.length > 0 && (
                    <Box sx={{
                        background: '#1a1a1a', border: '1px solid #2f2f2f',
                        borderRadius: '12px', p: 2, mb: 2,
                    }}>
                        {details.map(({ label, value, sub, mono }, idx) => (
                            <Box
                                key={label}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    ...(idx < details.length - 1 ? { pb: 1.2, mb: 1.2, borderBottom: '1px solid #252525' } : {}),
                                }}
                            >
                                <Typography sx={{ color: '#6a6a6a', fontSize: 12 }}>{label}</Typography>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography sx={{
                                        color: mono ? '#5a5a5a' : '#e0e0e0',
                                        fontSize: mono ? 11 : 13,
                                        fontWeight: mono ? 400 : 600,
                                        fontFamily: mono ? 'monospace' : undefined,
                                    }}>
                                        {value}
                                    </Typography>
                                    {sub && <Typography sx={{ color: '#5a5a5a', fontSize: 11 }}>{sub}</Typography>}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                {notice && (
                    <Box sx={{
                        background: '#f59e0b10', border: '1px solid #f59e0b30',
                        borderRadius: '10px', p: 1.5, mb: 2.5,
                        display: 'flex', gap: 1, alignItems: 'flex-start',
                    }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                            <path d="M12 4L2 20h20L12 4z" stroke="#f59e0b" strokeWidth="1.8" strokeLinejoin="round" />
                            <path d="M12 10v4M12 17v.5" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                        <Typography sx={{ color: '#f59e0b', fontSize: 12, lineHeight: 1.5 }}>{notice}</Typography>
                    </Box>
                )}

                {requiresTyping && (
                    <Box sx={{ mb: 2.5 }}>
                        <Typography sx={{ color: '#9a9a9a', fontSize: 12, mb: 1 }}>
                            {confirmationLabel || (
                                <>Type <strong style={{ color: cfg.accent }}>{confirmationText}</strong> to confirm</>
                            )}
                        </Typography>
                        <TextField
                            fullWidth
                            autoFocus
                            placeholder={confirmationText}
                            value={typedValue}
                            onChange={(e) => setTypedValue(e.target.value)}
                            disabled={loading}
                            sx={{
                                '& input': { color: '#fff', fontSize: 14, py: 1.2 },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    '& fieldset': { borderColor: '#333' },
                                    '&:hover fieldset': { borderColor: '#ef4444'},
                                    '&.Mui-focused fieldset': { borderColor: '#ef4444' },
                                },
                            }}
                        />
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1.2 }}>
                    <Button
                        fullWidth
                        onClick={onClose}
                        disabled={loading}
                        sx={{
                            border: '1px solid #333', borderRadius: '10px',
                            color: '#7a7a7a', fontWeight: 600, py: 1.3,
                            background: 'transparent',
                            '&:hover': { borderColor: '#555', color: '#ccc', background: 'transparent' },
                            '&:disabled': { opacity: 0.4 },
                        }}
                    >
                        {cancelText}
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        disabled={loading || isConfirmDisabled}
                        onClick={onConfirm}
                        sx={{
                            background: cfg.headerGradient,
                            borderRadius: '10px', fontWeight: 700, py: 1.3,
                            boxShadow: `0 4px 16px ${cfg.accent}35`,
                            '&:hover': { filter: 'brightness(0.9)' },
                            '&:disabled': { opacity: 0.5 },
                        }}
                    >
                        {loading
                            ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                            : confirmText
                        }
                    </Button>
                </Box>
            </Box>
        </Dialog>
    )
}

export default PopupDialog